import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, MagnifyingGlass, Funnel, Phone, Globe, ChatCircleDots, CheckCircle, XCircle, Eye, AirplaneTilt, Anchor } from '@phosphor-icons/react';
import { Autocomplete } from '../components/Autocomplete';
import { IncidentDetail } from '../components/incidents/IncidentDetail';

const API = '/api';

const SOURCE_ICONS = {
  call: Phone,
  website: Globe,
  whatsapp: ChatCircleDots
};

const SOURCE_LABELS = {
  call: 'Phone Call',
  website: 'Website',
  whatsapp: 'WhatsApp'
};

export default function Incidents() {
  const { user } = useAuth();
  const userSector = user?.sector || 'aviation';
  const canReview = user?.role === 'admin' || user?.role === 'iic';

  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSector, setFilterSector] = useState('all');
  const [reviewRemarks, setReviewRemarks] = useState('');

  const defaultSector = userSector === 'both' ? 'aviation' : userSector;

  const [formData, setFormData] = useState({
    sector: defaultSector,
    notification_source: 'website',
    incident_type: 'incident',
    date_occurred: new Date().toISOString().split('T')[0],
    time_occurred: '',
    location: '',
    description: '',
    severity: 'medium',
    reporter_name: '',
    reporter_phone: '',
    reporter_organization: '',
    aircraft_registration: '',
    aircraft_type: '',
    flight_number: '',
    operator_name: '',
    vessel_name: '',
    vessel_type: '',
    imo_number: '',
    vessel_flag: '',
    utc_date: '',
    utc_time: '',
    latitude: '',
    longitude: '',
    last_departure: '',
    planned_destination: '',
    mtow_kg: '',
    persons_on_board: '',
    dangerous_goods: '',
    dangerous_goods_details: ''
  });

  useEffect(() => { loadIncidents(); }, []);

  const loadIncidents = async () => {
    try {
      const { data } = await axios.get(`${API}/incidents`, { withCredentials: true });
      setIncidents(data);
    } catch (error) {
      console.error('Failed to load incidents:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      sector: defaultSector,
      notification_source: 'website',
      incident_type: 'incident',
      date_occurred: new Date().toISOString().split('T')[0],
      time_occurred: '',
      location: '',
      description: '',
      severity: 'medium',
      reporter_name: '',
      reporter_phone: '',
      reporter_organization: '',
      aircraft_registration: '',
      aircraft_type: '',
      flight_number: '',
      operator_name: '',
      vessel_name: '',
      vessel_type: '',
      imo_number: '',
      vessel_flag: '',
      utc_date: '',
      utc_time: '',
      latitude: '',
      longitude: '',
      last_departure: '',
      planned_destination: '',
      mtow_kg: '',
      persons_on_board: '',
      dangerous_goods: '',
      dangerous_goods_details: ''
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        mtow_kg: formData.mtow_kg === '' ? null : Number(formData.mtow_kg),
        persons_on_board: formData.persons_on_board === '' ? null : parseInt(formData.persons_on_board, 10),
      };
      await axios.post(`${API}/incidents/notify`, payload, { withCredentials: true });
      setIsCreateOpen(false);
      resetForm();
      loadIncidents();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error creating incident notification');
    }
  };

  const handleReview = async (incidentId, action) => {
    try {
      await axios.put(`${API}/incidents/${incidentId}/review`, {
        action,
        remarks: reviewRemarks
      }, { withCredentials: true });
      setReviewRemarks('');
      setIsDetailOpen(false);
      setSelectedIncident(null);
      loadIncidents();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error reviewing incident');
    }
  };

  const openDetail = (incident) => {
    setSelectedIncident(incident);
    setReviewRemarks('');
    setIsDetailOpen(true);
  };

  const filteredIncidents = useMemo(() => incidents.filter(inc => {
    const matchesSearch =
      inc.incident_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inc.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inc.aircraft_registration || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inc.vessel_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || inc.status === filterStatus;
    const matchesSector = filterSector === 'all' || inc.sector === filterSector;
    return matchesSearch && matchesStatus && matchesSector;
  }), [incidents, searchTerm, filterStatus, filterSector]);

  const summaryCounts = useMemo(() => ({
    reported: incidents.filter(i => i.status === 'reported').length,
    approved: incidents.filter(i => i.status === 'approved').length,
    rejected: incidents.filter(i => i.status === 'rejected').length,
  }), [incidents]);

  const getSeverityColor = (sev) => {
    const map = {
      critical: 'bg-red-600 text-white',
      high: 'bg-red-100 text-red-800',
      medium: 'bg-amber-100 text-amber-800',
      low: 'bg-green-100 text-green-800'
    };
    return map[sev] || 'bg-slate-100 text-slate-800';
  };

  const getStatusColor = (status) => {
    const map = {
      reported: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return map[status] || 'bg-slate-100 text-slate-800';
  };

  const isAviation = formData.sector === 'aviation';

  return (
    <div className="" data-testid="incidents-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
            Incident Notifications
          </h1>
          <p className="text-slate-500">ICAO 9756 Module 1 - Receive and manage incident/accident notifications</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg" data-testid="create-notification-btn">
              <Plus size={18} className="mr-2" />
              New Notification
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Log Incident Notification</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-5" data-testid="notification-form">
              {/* Sector & Source */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Investigation Sector *</Label>
                  {userSector === 'both' ? (
                    <Select value={formData.sector} onValueChange={(val) => setFormData({ ...formData, sector: val })}>
                      <SelectTrigger className="rounded-lg" data-testid="sector-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aviation">Aviation</SelectItem>
                        <SelectItem value="maritime">Maritime</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex items-center gap-2 h-10 px-3 border rounded-lg bg-slate-50">
                      {userSector === 'aviation' ? <AirplaneTilt size={18} className="text-blue-600" /> : <Anchor size={18} className="text-cyan-600" />}
                      <span className="text-sm font-medium capitalize">{userSector}</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Notification Source *</Label>
                  <Select value={formData.notification_source} onValueChange={(val) => setFormData({ ...formData, notification_source: val })}>
                    <SelectTrigger className="rounded-lg" data-testid="source-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Phone Call</SelectItem>
                      <SelectItem value="website">Website Report</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp Message</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sector indicator banner */}
              <div className={`p-3 rounded-lg border mt-1 ${isAviation ? 'bg-blue-50 border-blue-200' : 'bg-cyan-50 border-cyan-200'}`}>
                <p className={`text-xs font-medium ${isAviation ? 'text-blue-900' : 'text-cyan-900'}`}>
                  {isAviation ? 'Aviation Investigation' : 'Maritime Investigation'} - {SOURCE_LABELS[formData.notification_source]} notification
                </p>
              </div>

              {/* Reporter Info */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Reporter Information</Label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Name</Label>
                    <Input value={formData.reporter_name} onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value })} placeholder="Reporter name" className="rounded-lg" data-testid="reporter-name" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={formData.reporter_phone} onChange={(e) => setFormData({ ...formData, reporter_phone: e.target.value })} placeholder="Phone number" className="rounded-lg" data-testid="reporter-phone" />
                  </div>
                  <div>
                    <Label>Organization</Label>
                    <Input value={formData.reporter_organization} onChange={(e) => setFormData({ ...formData, reporter_organization: e.target.value })} placeholder="Organization" className="rounded-lg" data-testid="reporter-org" />
                  </div>
                </div>
              </div>

              {/* Event Details */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Event Type *</Label>
                  <Select value={formData.incident_type} onValueChange={(val) => setFormData({ ...formData, incident_type: val })}>
                    <SelectTrigger className="rounded-lg" data-testid="type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="accident">Accident</SelectItem>
                      <SelectItem value="incident">Incident</SelectItem>
                      <SelectItem value="serious_incident">Serious Incident</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date Occurred *</Label>
                  <Input type="date" value={formData.date_occurred} onChange={(e) => setFormData({ ...formData, date_occurred: e.target.value })} className="rounded-lg" required data-testid="date-field" />
                </div>
                <div>
                  <Label>Local Time</Label>
                  <Input type="time" value={formData.time_occurred} onChange={(e) => setFormData({ ...formData, time_occurred: e.target.value })} className="rounded-lg" data-testid="time-field" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{isAviation ? 'Location / Airport' : 'Location / Port'} *</Label>
                  <Autocomplete
                    endpoint={isAviation ? '/reference/airports' : '/reference/ports'}
                    value={formData.location}
                    onChange={(val) => setFormData({ ...formData, location: val })}
                    placeholder={isAviation ? "Type airport name or code..." : "Type port name or code..."}
                    data-testid="location-field"
                    onSelect={(item) => {
                      if (isAviation) {
                        setFormData({ ...formData, location: `${item.iata} / ${item.icao} - ${item.name}, ${item.city}, ${item.country}` });
                      } else {
                        setFormData({ ...formData, location: `${item.code} - ${item.name}, ${item.city}, ${item.country}` });
                      }
                    }}
                    renderItem={(item) => isAviation ? (
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-semibold text-blue-700">{item.iata}</span>
                          <span className="text-slate-400 mx-1">/</span>
                          <span className="font-medium text-slate-600">{item.icao}</span>
                          <span className="text-slate-400 mx-1.5">-</span>
                          <span className="text-slate-800">{item.name}</span>
                        </div>
                        <span className="text-xs text-slate-400 ml-2 shrink-0">{item.city}, {item.country}</span>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-semibold text-cyan-700">{item.code}</span>
                          <span className="text-slate-400 mx-1.5">-</span>
                          <span className="text-slate-800">{item.name}</span>
                        </div>
                        <span className="text-xs text-slate-400 ml-2 shrink-0">{item.city}, {item.country}</span>
                      </div>
                    )}
                  />
                </div>
                <div>
                  <Label>Severity *</Label>
                  <Select value={formData.severity} onValueChange={(val) => setFormData({ ...formData, severity: val })}>
                    <SelectTrigger className="rounded-lg" data-testid="severity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sector-specific fields */}
              {isAviation ? (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Aircraft Information</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Aircraft Registration</Label>
                      <Autocomplete
                        endpoint="/reference/aircraft-registrations"
                        value={formData.aircraft_registration}
                        onChange={(val) => setFormData({ ...formData, aircraft_registration: val })}
                        placeholder="Type registration (e.g., VT-ANB)..."
                        data-testid="aircraft-reg"
                        onSelect={(item) => {
                          setFormData({
                            ...formData,
                            aircraft_registration: item.registration,
                            aircraft_type: `${item.aircraft_type} (${item.type_code})`,
                            operator_name: item.operator
                          });
                        }}
                        renderItem={(item) => (
                          <div className="flex justify-between items-center gap-2">
                            <div>
                              <span className="font-mono font-bold text-blue-700">{item.registration}</span>
                              <span className="text-slate-400 mx-1.5">|</span>
                              <span className="text-slate-700">{item.aircraft_type}</span>
                              <span className="text-xs text-blue-500 ml-1 font-mono">{item.type_code}</span>
                            </div>
                            <span className="text-xs text-slate-400 shrink-0">{item.operator}</span>
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <Label>Aircraft Type</Label>
                      <Autocomplete
                        endpoint="/reference/aircraft-types"
                        value={formData.aircraft_type}
                        onChange={(val) => setFormData({ ...formData, aircraft_type: val })}
                        placeholder="Type aircraft model..."
                        data-testid="aircraft-type"
                        onSelect={(item) => {
                          setFormData({ ...formData, aircraft_type: `${item.name} (${item.code})` });
                        }}
                        renderItem={(item) => (
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-semibold text-slate-800">{item.name}</span>
                              <span className="text-xs text-blue-600 ml-2 font-mono">{item.code}</span>
                            </div>
                            <span className="text-xs text-slate-400 ml-2 shrink-0">{item.category}</span>
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <Label>Flight Number</Label>
                      <Input value={formData.flight_number} onChange={(e) => setFormData({ ...formData, flight_number: e.target.value })} placeholder="e.g., AI123" className="rounded-lg" data-testid="flight-number" />
                    </div>
                    <div>
                      <Label>Operator</Label>
                      <Autocomplete
                        endpoint="/reference/airlines"
                        value={formData.operator_name}
                        onChange={(val) => setFormData({ ...formData, operator_name: val })}
                        placeholder="Type airline / operator name..."
                        data-testid="operator-name"
                        onSelect={(item) => {
                          setFormData({ ...formData, operator_name: item.name });
                        }}
                        renderItem={(item) => (
                          <div className="flex justify-between items-center gap-2">
                            <div>
                              <span className="font-semibold text-slate-800">{item.name}</span>
                              {item.iata && <span className="text-xs text-blue-600 ml-1.5 font-mono">{item.iata}</span>}
                              <span className="text-xs text-slate-400 ml-0.5 font-mono">/ {item.icao}</span>
                            </div>
                            <span className="text-xs text-slate-400 shrink-0">{item.country}</span>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mt-5 mb-2 block">ICAO Notification Details (Annex 13, 4.2)</Label>
                  <div className="grid grid-cols-2 gap-3" data-testid="annex13-fields">
                    <div>
                      <Label>UTC Date</Label>
                      <Input type="date" value={formData.utc_date} onChange={(e) => setFormData({ ...formData, utc_date: e.target.value })} className="rounded-lg" />
                    </div>
                    <div>
                      <Label>UTC Time</Label>
                      <Input type="time" value={formData.utc_time} onChange={(e) => setFormData({ ...formData, utc_time: e.target.value })} className="rounded-lg" />
                    </div>
                    <div>
                      <Label>Last Point of Departure</Label>
                      <Input value={formData.last_departure} onChange={(e) => setFormData({ ...formData, last_departure: e.target.value })} placeholder="ICAO code, e.g. VIDP" className="rounded-lg" />
                    </div>
                    <div>
                      <Label>Point of Intended Landing</Label>
                      <Input value={formData.planned_destination} onChange={(e) => setFormData({ ...formData, planned_destination: e.target.value })} placeholder="ICAO code, e.g. VABB" className="rounded-lg" />
                    </div>
                    <div>
                      <Label>Latitude</Label>
                      <Input value={formData.latitude} onChange={(e) => setFormData({ ...formData, latitude: e.target.value })} placeholder="Decimal degrees" className="rounded-lg" />
                    </div>
                    <div>
                      <Label>Longitude</Label>
                      <Input value={formData.longitude} onChange={(e) => setFormData({ ...formData, longitude: e.target.value })} placeholder="Decimal degrees" className="rounded-lg" />
                    </div>
                    <div>
                      <Label>Max Take-off Mass (kg)</Label>
                      <Input type="number" min="0" value={formData.mtow_kg} onChange={(e) => setFormData({ ...formData, mtow_kg: e.target.value })} className="rounded-lg" />
                    </div>
                    <div>
                      <Label>Persons on Board</Label>
                      <Input type="number" min="0" value={formData.persons_on_board} onChange={(e) => setFormData({ ...formData, persons_on_board: e.target.value })} className="rounded-lg" />
                    </div>
                    <div>
                      <Label>Dangerous Goods on Board</Label>
                      <Select value={formData.dangerous_goods || undefined} onValueChange={(val) => setFormData({ ...formData, dangerous_goods: val })}>
                        <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="No">No</SelectItem>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="Unknown">Unknown</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.dangerous_goods === 'Yes' && (
                      <div>
                        <Label>Dangerous Goods Details</Label>
                        <Input value={formData.dangerous_goods_details} onChange={(e) => setFormData({ ...formData, dangerous_goods_details: e.target.value })} className="rounded-lg" />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Vessel Information</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Vessel Name</Label>
                      <Autocomplete
                        endpoint="/reference/vessel-registrations"
                        value={formData.vessel_name}
                        onChange={(val) => setFormData({ ...formData, vessel_name: val })}
                        placeholder="Type vessel name or IMO..."
                        data-testid="vessel-name"
                        onSelect={(item) => {
                          setFormData({
                            ...formData,
                            vessel_name: item.name,
                            imo_number: item.imo,
                            vessel_flag: item.flag,
                            vessel_type: item.vessel_type
                          });
                        }}
                        renderItem={(item) => (
                          <div className="flex justify-between items-center gap-2">
                            <div>
                              <span className="font-semibold text-cyan-700">{item.name}</span>
                              <span className="text-slate-400 mx-1.5">|</span>
                              <span className="text-xs font-mono text-slate-600">IMO {item.imo}</span>
                            </div>
                            <span className="text-xs text-slate-400 shrink-0">{item.flag} - {item.operator}</span>
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <Label>Vessel Type</Label>
                      <Autocomplete
                        endpoint="/reference/vessel-types"
                        value={formData.vessel_type}
                        onChange={(val) => setFormData({ ...formData, vessel_type: val })}
                        placeholder="Type vessel type..."
                        data-testid="vessel-type"
                        onSelect={(item) => {
                          setFormData({ ...formData, vessel_type: `${item.name} (${item.code})` });
                        }}
                        renderItem={(item) => (
                          <div>
                            <div className="font-semibold text-slate-800">{item.name} <span className="text-xs font-mono text-cyan-600">{item.code}</span></div>
                            <div className="text-xs text-slate-400">{item.description}</div>
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <Label>IMO Number</Label>
                      <Autocomplete
                        endpoint="/reference/vessel-registrations"
                        value={formData.imo_number}
                        onChange={(val) => setFormData({ ...formData, imo_number: val })}
                        placeholder="Type IMO number..."
                        data-testid="imo-number"
                        onSelect={(item) => {
                          setFormData({
                            ...formData,
                            vessel_name: item.name,
                            imo_number: item.imo,
                            vessel_flag: item.flag,
                            vessel_type: item.vessel_type
                          });
                        }}
                        renderItem={(item) => (
                          <div className="flex justify-between items-center gap-2">
                            <div>
                              <span className="font-mono font-bold text-cyan-700">IMO {item.imo}</span>
                              <span className="text-slate-400 mx-1.5">|</span>
                              <span className="text-slate-700">{item.name}</span>
                            </div>
                            <span className="text-xs text-slate-400 shrink-0">{item.flag}</span>
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <Label>Vessel Flag (Registered Country)</Label>
                      <Autocomplete
                        endpoint="/reference/vessel-flags"
                        value={formData.vessel_flag}
                        onChange={(val) => setFormData({ ...formData, vessel_flag: val })}
                        placeholder="Type country name..."
                        data-testid="vessel-flag"
                        onSelect={(item) => {
                          setFormData({ ...formData, vessel_flag: item.country });
                        }}
                        renderItem={(item) => (
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-semibold text-slate-800">{item.country}</span>
                              <span className="text-xs text-cyan-600 ml-2 font-mono">{item.code}</span>
                            </div>
                            <span className="text-xs text-slate-400 ml-2 shrink-0">{item.registry}</span>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label>Description *</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Detailed description of the event..." className="rounded-lg min-h-[100px]" required data-testid="description-field" />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg" data-testid="submit-notification-btn">
                  Submit Notification
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)} className="rounded-lg">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search incidents..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 rounded-lg" data-testid="search-incidents" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-44 rounded-lg" data-testid="filter-status">
              <Funnel size={16} className="mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="reported">Reported</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          {(userSector === 'both' || user?.role === 'admin') && (
            <Select value={filterSector} onValueChange={setFilterSector}>
              <SelectTrigger className="w-44 rounded-lg" data-testid="filter-sector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                <SelectItem value="aviation">Aviation</SelectItem>
                <SelectItem value="maritime">Maritime</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200" data-testid="count-reported">
          <div className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Outfit' }}>{summaryCounts.reported}</div>
          <div className="text-sm text-slate-500">Pending Review</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200" data-testid="count-approved">
          <div className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Outfit' }}>{summaryCounts.approved}</div>
          <div className="text-sm text-slate-500">Approved for Investigation</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200" data-testid="count-rejected">
          <div className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Outfit' }}>{summaryCounts.rejected}</div>
          <div className="text-sm text-slate-500">Rejected</div>
        </div>
      </div>

      {/* Incidents Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading notifications...</div>
        ) : filteredIncidents.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            {searchTerm || filterStatus !== 'all' ? 'No incidents match your filters' : 'No incident notifications yet. Click "New Notification" to log one.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="incidents-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Incident #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Sector</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">{isAviation ? 'Aircraft/Vessel' : 'Vessel/Aircraft'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Severity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredIncidents.map((inc) => {
                  const SourceIcon = SOURCE_ICONS[inc.notification_source] || Globe;
                  const identifier = inc.sector === 'aviation'
                    ? (inc.aircraft_registration || inc.flight_number || '-')
                    : (inc.vessel_name || inc.imo_number || '-');
                  return (
                    <tr key={inc.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`incident-row-${inc.id}`}>
                      <td className="px-4 py-3 font-mono text-sm font-medium text-blue-600">{inc.incident_number}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${inc.sector === 'aviation' ? 'bg-blue-100 text-blue-800' : 'bg-cyan-100 text-cyan-800'}`}>
                          {inc.sector === 'aviation' ? 'AVN' : 'MAR'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <SourceIcon size={16} className="text-slate-500" />
                          <span className="text-xs text-slate-600">{SOURCE_LABELS[inc.notification_source] || inc.notification_source}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm capitalize">{inc.incident_type?.replace('_', ' ')}</td>
                      <td className="px-4 py-3 text-sm font-mono">{inc.date_occurred}</td>
                      <td className="px-4 py-3 text-sm">{identifier}</td>
                      <td className="px-4 py-3 text-sm max-w-[150px] truncate">{inc.location}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(inc.severity)}`}>{inc.severity}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(inc.status)}`}>{inc.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="rounded-lg h-8 w-8 p-0" onClick={() => openDetail(inc)} data-testid={`view-incident-${inc.id}`}>
                            <Eye size={14} />
                          </Button>
                          {canReview && inc.status === 'reported' && (
                            <>
                              <Button size="sm" className="rounded-lg h-8 w-8 p-0 bg-green-600 hover:bg-green-700 text-white" onClick={() => handleReview(inc.id, 'approve')} data-testid={`approve-incident-${inc.id}`}>
                                <CheckCircle size={14} />
                              </Button>
                              <Button size="sm" className="rounded-lg h-8 w-8 p-0 bg-red-500 hover:bg-red-600 text-white" onClick={() => handleReview(inc.id, 'reject')} data-testid={`reject-incident-${inc.id}`}>
                                <XCircle size={14} />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <IncidentDetail
        incident={selectedIncident}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        canReview={canReview}
        reviewRemarks={reviewRemarks}
        setReviewRemarks={setReviewRemarks}
        onReview={handleReview}
      />
    </div>
  );
}
